import React, { useRef } from "react";
import {
  Dialog, DialogContent, DialogTitle, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Box, Divider, Button
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import Index from "../../../Index";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EntryTable = ({ rows }) => (
  <Table size="small" sx={{ mb: 1 }}>
    <TableHead>
      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
        {["Date", "Account Name", "Voucher No.", "Chq. No.", "Amount"].map((h) => (
          <TableCell key={h} sx={{ fontWeight: 600, fontSize: 11, py: 0.5 }}>{h}</TableCell>
        ))}
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((r, i) => (
        <TableRow key={i}>
          <TableCell sx={{ fontSize: 11, py: 0.3 }}>{moment(r.voucherDate).format("DD/MM/YYYY")}</TableCell>
          <TableCell sx={{ fontSize: 11, py: 0.3 }}>{r.accountName}</TableCell>
          <TableCell sx={{ fontSize: 11, py: 0.3 }}>{r.voucherNo}</TableCell>
          <TableCell sx={{ fontSize: 11, py: 0.3 }}>{r.chequeNo || "-"}</TableCell>
          <TableCell sx={{ fontSize: 11, py: 0.3, textAlign: "right" }}>{fmt(r.amount)}</TableCell>
        </TableRow>
      ))}
      {/* Total row */}
      <TableRow sx={{ backgroundColor: "#fafafa" }}>
        <TableCell colSpan={4} sx={{ fontSize: 11, py: 0.3, fontWeight: 600, textAlign: "right" }}>Total</TableCell>
        <TableCell sx={{ fontSize: 11, py: 0.3, fontWeight: 600, textAlign: "right" }}>
          {fmt(rows.reduce((s, r) => s + Number(r.amount || 0), 0))}
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
);

const Row = ({ label, amount, drCr, bold, indent, color }) => (
  <Box sx={{
    display: "flex",
    justifyContent: "space-between",
    mt: 0.5,
    pl: indent ? 2 : 0,
  }}>
    <Typography sx={{ fontSize: 12, fontWeight: bold ? 700 : 400, color: color || "inherit" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 12, fontWeight: bold ? 700 : 400, color: color || "inherit" }}>
      {amount !== undefined ? `${fmt(amount)}${drCr ? " " + drCr : ""}` : ""}
    </Typography>
  </Box>
);

const BankRecoStatement = ({ open, onClose, data, bankName, fromDate, toDate, companyAddress }) => {

  const printRef = useRef();

  const handlePrint = useReactToPrint({ content: () => printRef.current });

  if (!data) return null;

  const bpEntries = data.bpEntries || [];
  const brEntries = data.brEntries || [];

  const rawBalance = Number(data.balance?.bankBookBalance ?? 0);
  const isDR = rawBalance >= 0;
  const balanceDRCR = isDR ? "DR" : "CR";
  const balanceAmt = Math.abs(rawBalance);

  const bpTotal = bpEntries.reduce((s, r) => s + Number(r.amount || 0), 0);
  const brTotal = brEntries.reduce((s, r) => s + Number(r.amount || 0), 0);

  // Passbook = BankBook ± uncleared
  // DR balance: Add BP (issued not cleared), Less BR (deposited not cleared)
  // CR balance: Less BP, Add BR
  const passbookRaw = isDR
    ? rawBalance + bpTotal - brTotal
    : rawBalance - bpTotal + brTotal;

  const passbookAmt = Math.abs(passbookRaw);
  const passbookDRCR = passbookRaw >= 0 ? "DR" : "CR";

  const printStyles = `
    @media print {
      @page { size: A4; margin: 15mm; }
      body { -webkit-print-color-adjust: exact; }
    }
  `;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
        <Typography variant="h6" sx={{ fontSize: 15 }}>Bank Reconciliation Statement</Typography>

      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <style>{printStyles}</style>
        <Box ref={printRef} sx={{ p: 3 }}>

          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 17 }}>{companyAddress?.Company_name || "Company name"}</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Bank Reconciliation Statement</Typography>
            <Typography sx={{ fontSize: 12, mt: 0.5 }}>
              Bank: <strong>{bankName}</strong>
            </Typography>
            <Typography sx={{ fontSize: 12 }}>
              From&nbsp;<strong>{moment(fromDate).format("DD/MM/YYYY")}</strong>
              &nbsp;To&nbsp;<strong>{moment(toDate).format("DD/MM/YYYY")}</strong>
            </Typography>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          {/* Bank Book Balance */}
          <Row label="Balance as per Bank Book" amount={balanceAmt} drCr={balanceDRCR} bold />

          <Divider sx={{ my: 1 }} />

          {/* BP Section */}
          <Typography sx={{ fontWeight: 700, fontSize: 12, mt: 1.5, mb: 0.5 }}>
            {isDR ? "Add" : "Less"} : Cheque(s) Issued but Not Cleared (Bank Payments)
          </Typography>
          {bpEntries.length > 0 ? (
            <EntryTable rows={bpEntries} />
          ) : (
            <Typography sx={{ fontSize: 11, ml: 2, color: "text.secondary", mb: 1 }}>
              -------------- Nil ---------------
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />

          {/* BR Section */}
          <Typography sx={{ fontWeight: 700, fontSize: 12, mt: 1.5, mb: 0.5 }}>
            {isDR ? "Less" : "Add"} : Cheque(s) Deposited but Not Cleared (Bank Receipts)
          </Typography>
          {brEntries.length > 0 ? (
            <EntryTable rows={brEntries} />
          ) : (
            <Typography sx={{ fontSize: 11, ml: 2, color: "text.secondary", mb: 1 }}>
              -------------- Nil ---------------
            </Typography>
          )}

          <Divider sx={{ mt: 2, mb: 1 }} />

          {/* Passbook Balance */}
          <Row
            label="Balance as per Pass Book / Bank Statement"
            amount={passbookAmt}
            drCr={passbookDRCR}
            bold
            color={passbookDRCR === "CR" ? "#c62828" : "#1b5e20"}
          />

        </Box>
      </DialogContent>


      <Index.Box className="btn-flex-end" sx={{ px: 2, py: 1.5 }}>
        <Index.Box className="btn-primary-main">
          <Index.Button
            startIcon={<PrintIcon />}
            variant=""
            className="btn-primary"
            type="submit"
            onClick={handlePrint}
          >
            Print
          </Index.Button>
        </Index.Box>
      </Index.Box>
    </Dialog>
  );
};

export default BankRecoStatement;